/**
 * La logique pure du Tableau — tri et pagination — sans React, testée à part.
 */

export type SensTri = "asc" | "desc";

export interface Tri {
  cle: string;
  sens: SensTri;
}

/** Un clic sur un en-tête : rien → croissant → décroissant → rien. Avec `descDabord` (un chiffre : le plus grand
 *  d'abord) : rien → décroissant → croissant → rien. */
export function suivantTri(tri: Tri | null | undefined, cle: string, descDabord = false): Tri | null {
  const premier: SensTri = descDabord ? "desc" : "asc";
  if (!tri || tri.cle !== cle) return { cle, sens: premier };
  if (tri.sens === premier) return { cle, sens: premier === "asc" ? "desc" : "asc" };
  return null;
}

export type ValeurTriable = string | number | boolean | Date | null | undefined;

/** Ce que compare le tri pour une colonne : `valeur(ligne)` si la colonne le dit, sinon la propriété `cle`. */
export interface ColonneTriable<T> {
  cle: string;
  valeur?: (ligne: T) => ValeurTriable;
}

export function valeurDe<T>(ligne: T, colonne: ColonneTriable<T>): ValeurTriable {
  if (colonne.valeur) return colonne.valeur(ligne);
  return (ligne as Record<string, unknown>)[colonne.cle] as ValeurTriable;
}

const collator = new Intl.Collator("fr", { numeric: true, sensitivity: "base" });

function comparer(a: ValeurTriable, b: ValeurTriable): number {
  const aVide = a === null || a === undefined || a === "";
  const bVide = b === null || b === undefined || b === "";
  if (aVide && bVide) return 0;
  if (aVide) return 1; // les vides toujours en bas, quel que soit le sens
  if (bVide) return -1;
  if (a instanceof Date && b instanceof Date) return a.getTime() - b.getTime();
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return Number(a) - Number(b);
  return collator.compare(String(a), String(b));
}

/** Rend une COPIE triée ; stable ; les vides restent en bas même en décroissant. */
export function trierLignes<T>(lignes: readonly T[], tri: Tri | null | undefined, colonnes: readonly ColonneTriable<T>[]): T[] {
  if (!tri) return [...lignes];
  const colonne = colonnes.find((c) => c.cle === tri.cle);
  if (!colonne) return [...lignes];
  const sens = tri.sens === "asc" ? 1 : -1;
  return lignes
    .map((ligne, i) => ({ ligne, i, v: valeurDe(ligne, colonne) }))
    .sort((x, y) => {
      const xVide = x.v === null || x.v === undefined || x.v === "";
      const yVide = y.v === null || y.v === undefined || y.v === "";
      if (xVide !== yVide) return xVide ? 1 : -1;
      const c = comparer(x.v, y.v) * sens;
      return c !== 0 ? c : x.i - y.i;
    })
    .map((x) => x.ligne);
}

/** Ce qu'une colonne dit de sa place selon la largeur du tableau (cf `Colonne` dans `Tableau.tsx`). */
export interface ColonneSelonLargeur {
  cle: string;
  /** S'efface quand le tableau est étroit. */
  secondaire?: boolean;
  /** En étroit, seules les `essentiel` restent, s'il y en a. */
  essentiel?: boolean;
  /** Ne s'affiche qu'à partir de cette largeur du tableau (px). */
  desDe?: number;
}

/**
 * Les colonnes qu'une largeur de tableau laisse voir — trois tailles :
 *   - sous `seuilEtroit` (l'étroit) : les `essentiel` seules s'il y en a, sinon tout sauf les `secondaire` ;
 *   - au-dessus (le large) : toutes…
 *   - … sauf qu'une colonne `desDe` attend que le tableau fasse au moins cette largeur, étroit ou large :
 *     la troisième taille, entre les deux (la Créatrice de Comptes n'a sa place que sur un grand panneau,
 *     jamais au prix du @).
 * Une largeur à 0 (pas encore mesuré, un onglet caché, les tests) n'est pas « étroit » : toutes les colonnes.
 */
export function colonnesVisibles<C extends ColonneSelonLargeur>(
  colonnes: readonly C[],
  largeur: number,
  seuilEtroit: number,
): C[] {
  if (largeur <= 0) return [...colonnes];
  const assezLarge = (c: C) => c.desDe === undefined || largeur >= c.desDe;
  if (largeur >= seuilEtroit) return colonnes.filter(assezLarge);
  const essentielles = colonnes.filter((c) => c.essentiel);
  return (essentielles.length > 0 ? essentielles : colonnes.filter((c) => !c.secondaire)).filter(assezLarge);
}

/**
 * Les pages à montrer dans le pied : au plus 7 cases, `null` = « … ».
 *   pages ≤ 7        → 1 2 3 4 5 6 7
 *   page près du début → 1 2 3 4 5 … 18
 *   page au milieu     → 1 … 8 9 10 … 18
 *   page près de la fin → 1 … 14 15 16 17 18
 */
export function pagesAffichees(page: number, pages: number): (number | null)[] {
  const suite = (a: number, b: number) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
  if (pages <= 7) return suite(1, Math.max(1, pages));
  if (page <= 4) return [...suite(1, 5), null, pages];
  if (page >= pages - 3) return [1, null, ...suite(pages - 4, pages)];
  return [1, null, page - 1, page, page + 1, null, pages];
}
